import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Divider,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Radio,
  Snackbar,
  DialogContentText,
  FormHelperText
} from '@mui/material';
import { ArrowBack, Phone, Email, LocationOn, Edit, Delete, Send as SendIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import countries from '../data/countries';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useOrganizationName from '../hooks/useOrganizationName';

const FirstTimerDetail = () => {
  const { id, organizationSlug } = useParams();
  const navigate = useNavigate();
  const { organizationSlug: authOrgSlug } = useAuth();
  const currentOrgSlug = organizationSlug || authOrgSlug;
  const { organizationName } = useOrganizationName();
  const [firstTimer, setFirstTimer] = useState(null);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [followUpType, setFollowUpType] = useState('phone_call');
  const [contactedBy, setContactedBy] = useState('');
  const [status, setStatus] = useState('contacted');
  const [followUpDate, setFollowUpDate] = useState(new Date());
  const [recommendation, setRecommendation] = useState('');
  const [notes, setNotes] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = JSON.parse(localStorage.getItem('user'));
  const [completionDialog, setCompletionDialog] = useState(false);
  const [tempFollowUpData, setTempFollowUpData] = useState(null);
  const [completionReason, setCompletionReason] = useState('');
  const [completionConfirmed, setCompletionConfirmed] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [detailsResponse, historyResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/follow-up/detail/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/follow-up/history/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);

        const [detailsData, historyData] = await Promise.all([
          detailsResponse.json(),
          historyResponse.json()
        ]);

        if (detailsResponse.ok && historyResponse.ok) {
          setFirstTimer(detailsData);
          setFollowUpHistory(historyData);
        } else {
          setError(detailsData.error || historyData.error);
        }
      } catch (error) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleEditFollowUp = (followUp) => {
    setEditingFollowUp(followUp);
    setFollowUpType(followUp.follow_up_type);
    setContactedBy(followUp.contacted_by);
    setStatus(followUp.status);
    setFollowUpDate(new Date(followUp.follow_up_date));
    setRecommendation(followUp.recommendation);
    setNotes(followUp.notes);
    setShowFollowUpDialog(true);
  };

  const handleDialogClose = () => {
    setShowFollowUpDialog(false);
    setEditingFollowUp(null);
    setFollowUpType('phone_call');
    setContactedBy('');
    setStatus('pending');
    setFollowUpDate(new Date());
    setRecommendation('');
    setNotes('');
  };

  const handleNewFollowUp = async (followUpData) => {
    try {
      const url = editingFollowUp 
        ? `${process.env.REACT_APP_API_URL}/api/follow-up/edit/${editingFollowUp.id}`
        : `${process.env.REACT_APP_API_URL}/api/follow-up/${id}`;

      const method = editingFollowUp ? 'PUT' : 'POST';

      const response = await fetch(
        url,
        {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(followUpData)
        }
      );

      if (response.ok) {
        handleDialogClose();
        const historyResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/follow-up/history/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const historyData = await historyResponse.json();
        if (historyResponse.ok) {
          setFollowUpHistory(historyData);
        }

        const detailsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/follow-up/detail/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const detailsData = await detailsResponse.json();
        if (detailsResponse.ok) {
          setFirstTimer(detailsData);
        }
      } else {
        const errorData = await response.json();
        console.error('Error saving follow-up:', errorData);
        alert(errorData.error || 'Failed to save follow-up');
      }
    } catch (error) {
      console.error('Error saving follow-up:', error);
      alert('Failed to save follow-up. Please try again.');
    }
  };

  const handleEditClick = () => {
    setEditData(firstTimer);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditData(null);
  };

  const handleEditSave = async () => {
    try {
      // If not a student, ensure school field is empty string
      const dataToSend = {
        ...editData,
        school: editData.is_student ? (editData.school || '') : '',
        campus_location: editData.is_student ? (editData.campus_location || '') : '',
        campus_duration: editData.is_student ? (editData.campus_duration || '') : ''
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/first-timer/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        const updatedData = await response.json();
        setFirstTimer(updatedData);
        handleEditClose();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update first timer');
      }
    } catch (error) {
      console.error('Error updating first timer:', error);
      alert('Failed to update first timer');
    }
  };

  const handleShowFollowUpDialog = () => {
    setShowFollowUpDialog(true);
    setContactedBy(`${user?.first_name} ${user?.last_name}`);
    setStatus('contacted');
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/first-timer/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete first timer');
      }

      setSnackbarMessage('First timer deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // Redirect to dashboard with organization context
      const dashboardPath = currentOrgSlug ? `/${currentOrgSlug}/dashboard` : '/dashboard';
      navigate(dashboardPath);
    } catch (error) {
      console.error('Error deleting first timer:', error);
      setSnackbarMessage('Failed to delete first timer');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === 'completed' && !completionConfirmed) {
      setTempFollowUpData({
        follow_up_type: followUpType,
        contacted_by: contactedBy,
        status: newStatus,
        recommendation,
        follow_up_date: followUpDate,
        notes
      });
      setCompletionDialog(true);
    } else {
      setStatus(newStatus);
    }
  };

  const handleCompletionConfirm = () => {
    if (!completionReason) {
      return;
    }
    setStatus('completed');
    setRecommendation(completionReason);
    setCompletionConfirmed(true);
    setCompletionDialog(false);
    // Add completion reason to notes if there are existing notes
    const updatedNotes = notes 
      ? `${notes}\n\nCompletion Reason: ${completionReason}` 
      : `Completion Reason: ${completionReason}`;
    setNotes(updatedNotes);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => {
            const dashboardPath = currentOrgSlug ? `/${currentOrgSlug}/dashboard` : '/dashboard';
            navigate(dashboardPath);
          }}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5">First Timer Details</Typography>
            <Box>
              <IconButton 
                onClick={() => navigate(`/email-composer?audienceType=first_timers&recipientEmails=${firstTimer?.email || ''}`)} 
                color="info" 
                sx={{ mr: 1 }}
                title="Send Email"
              >
                <SendIcon />
              </IconButton>
              <IconButton onClick={handleEditClick} color="primary" sx={{ mr: 1 }}>
                <Edit />
              </IconButton>
              <IconButton onClick={handleDeleteClick} color="error">
                <Delete />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="h4" gutterBottom>
            {`${firstTimer.first_name} ${firstTimer.last_name}`}
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Contact Information</Typography>
                  <List dense>
                    <ListItem>
                      <Phone sx={{ mr: 2 }} />
                      <ListItemText 
                        primary="Phone"
                        secondary={
                          <Button href={`tel:${firstTimer.phone_number}`}>
                            {firstTimer.phone_number}
                          </Button>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <Email sx={{ mr: 2 }} />
                      <ListItemText 
                        primary="Email"
                        secondary={
                          <Button href={`mailto:${firstTimer.email}`}>
                            {firstTimer.email}
                          </Button>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <LocationOn sx={{ mr: 2 }} />
                      <ListItemText 
                        primary="Address"
                        secondary={
                          <Typography variant="body1" gutterBottom>
                            <strong>Address:</strong><br />
                            {firstTimer.address_line1}
                            {firstTimer.address_line2 && <><br />{firstTimer.address_line2}</>}
                            <br />
                            {firstTimer.city}, {firstTimer.postal_code}
                            <br />
                            {firstTimer.country}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Personal Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="First Visit Date"
                        secondary={new Date(firstTimer.service_date).toLocaleDateString()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Gender"
                        secondary={firstTimer.gender}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Student"
                        secondary={firstTimer.is_student ? `Yes${firstTimer.school ? ` - ${firstTimer.school}` : ''}` : 'No'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Visiting Member"
                        secondary={firstTimer.is_visiting_member ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Needs Transportation"
                        secondary={firstTimer.needs_transportation ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Marital Status"
                        secondary={firstTimer.marital_status || 'Not specified'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Relocating Winners Member"
                        secondary={firstTimer.is_relocating_member ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary={`${organizationName} Membership Interest`}
                        secondary={firstTimer.wants_wcito_membership ? 'Yes' : 'No'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Spiritual Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Given Life to Christ"
                        secondary={firstTimer.given_life_to_christ ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Water Baptized"
                        secondary={firstTimer.water_baptized ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText 
                        primary="Holy Ghost Baptized"
                        secondary={firstTimer.holy_ghost_baptized ? 'Yes' : 'No'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              {firstTimer.is_student && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Campus Information</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Campus Location"
                          secondary={firstTimer.campus_location || 'Not specified'}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemText 
                          primary="Campus Duration"
                          secondary={firstTimer.campus_duration?.replace('_', ' ') || 'Not specified'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Follow-up Status</Typography>
                  <Typography 
                    variant="subtitle1" 
                    color={
                      firstTimer.follow_up_status === 'completed' ? 'success.main' :
                      firstTimer.follow_up_status === 'contacted' ? 'info.main' :
                      firstTimer.follow_up_status === 'unreachable' ? 'error.main' :
                      'warning.main'
                    }
                    sx={{ mb: 2 }}
                  >
                    {firstTimer.follow_up_status || 'Pending'}
                  </Typography>
                  {firstTimer.last_follow_up_date && (
                    <Typography variant="body2" color="text.secondary">
                      Last followed up: {new Date(firstTimer.last_follow_up_date).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Additional Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">How they heard about us</Typography>
                    <Typography variant="body2">{firstTimer.heard_about_us || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2">Prayer Request</Typography>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {firstTimer.prayer_request || 'None provided'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Follow-up History</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleShowFollowUpDialog}
                    >
                      Add Follow-up
                    </Button>
                  </Box>
                  <List>
                    {followUpHistory.length > 0 ? (
                      followUpHistory.map((followUp) => (
                        <ListItem
                          key={followUp.id}
                          divider
                          sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                        >
                          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" color="primary">
                              {followUp.contacted_by_with_unit || followUp.contacted_by} - {followUp.follow_up_type ? followUp.follow_up_type.replace(/_/g, ' ') : 'N/A'}
                            </Typography>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(followUp.follow_up_date).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleEditFollowUp(followUp)}
                                sx={{ ml: 1 }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography
                            variant="body2"
                            color={
                              followUp.status === 'completed' ? 'success.main' :
                              followUp.status === 'contacted' ? 'info.main' :
                              followUp.status === 'unreachable' ? 'error.main' :
                              'warning.main'
                            }
                          >
                            Status: {followUp.status}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: 1 }}
                            color={
                              followUp.recommendation === 'established' ? 'success.main' :
                              followUp.recommendation === 'non_permanent_visitor' ? 'info.main' :
                              followUp.recommendation === 'not_reachable' ? 'error.main' :
                              'warning.main'
                            }
                          >
                            Recommendation: {followUp.recommendation ? followUp.recommendation.replace(/_/g, ' ') : 'None'}
                          </Typography>
                          {followUp.notes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Notes:</strong> {followUp.notes}
                            </Typography>
                          )}
                        </ListItem>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        No follow-up history available
                      </Typography>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Dialog 
        open={showFollowUpDialog} 
        onClose={handleDialogClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            minHeight: isMobile ? '100vh' : 'auto',
            margin: isMobile ? 0 : 32,
            maxHeight: isMobile ? '100vh' : 'calc(100vh - 64px)'
          }
        }}
      >
        <DialogTitle>{editingFollowUp ? 'Edit Follow-up' : 'Add New Follow-up'}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Follow-up Date"
              value={followUpDate}
              onChange={(newValue) => setFollowUpDate(newValue)}
              maxDate={new Date()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin="normal"
                  required
                  error={followUpDate > new Date()}
                  helperText={followUpDate > new Date() ? "Date cannot be in the future" : ""}
                />
              )}
            />
          </LocalizationProvider>
          
          <TextField
            select
            fullWidth
            margin="normal"
            label="Follow-up Type"
            value={followUpType}
            onChange={(e) => setFollowUpType(e.target.value)}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: isMobile ? '40vh' : 'auto',
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      paddingTop: 1,
                      paddingBottom: 1
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="phone_call">Phone Call</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="home_visit">Home Visit</MenuItem>
            <MenuItem value="homecell">Homecell</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          
          <TextField
            fullWidth
            margin="normal"
            label="Contacted By"
            value={contactedBy}
            onChange={(e) => setContactedBy(e.target.value)}
          />
          
          <TextField
            select
            fullWidth
            margin="normal"
            label="Status"
            value={status}
            onChange={handleStatusChange}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: isMobile ? '40vh' : 'auto',
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="contacted">Contacted</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="unreachable">Unreachable</MenuItem>
          </TextField>
          
          <TextField
            select
            fullWidth
            margin="normal"
            label="Recommendation"
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: isMobile ? '40vh' : 'auto',
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      paddingTop: 1,
                      paddingBottom: 1
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="established">Established</MenuItem>
            <MenuItem value="add_to_homecell">Add to Homecell</MenuItem>
            <MenuItem value="join_service_group">Join Service Group</MenuItem>
            <MenuItem value="more_follow_up">More Follow-up Needed</MenuItem>
            <MenuItem value="stronger_follow_up">Stronger Follow-up Required</MenuItem>
            <MenuItem value="non_permanent_visitor">Non-permanent Visitor</MenuItem>
            <MenuItem value="not_reachable">Not Reachable</MenuItem>
          </TextField>
          
          <TextField
            fullWidth
            margin="normal"
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={() => handleNewFollowUp({
              follow_up_type: followUpType,
              contacted_by: contactedBy,
              status,
              recommendation,
              follow_up_date: followUpDate,
              notes
            })} 
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle>Edit First Timer Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editData?.first_name || ''}
                onChange={(e) => setEditData({...editData, first_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editData?.last_name || ''}
                onChange={(e) => setEditData({...editData, last_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={editData?.email || ''}
                onChange={(e) => setEditData({...editData, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editData?.phone_number || ''}
                onChange={(e) => setEditData({...editData, phone_number: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={editData?.address_line1 || ''}
                onChange={(e) => setEditData({...editData, address_line1: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={editData?.address_line2 || ''}
                onChange={(e) => setEditData({...editData, address_line2: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={editData?.city || ''}
                onChange={(e) => setEditData({...editData, city: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={editData?.postal_code || ''}
                onChange={(e) => setEditData({...editData, postal_code: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Country"
                value={editData?.country || 'Canada'}
                onChange={(e) => setEditData({...editData, country: e.target.value})}
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Gender"
                value={editData?.gender || ''}
                onChange={(e) => setEditData({...editData, gender: e.target.value})}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="How did you hear about us?"
                value={editData?.heard_about_us || ''}
                onChange={(e) => setEditData({...editData, heard_about_us: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Prayer Request"
                value={editData?.prayer_request || ''}
                onChange={(e) => setEditData({...editData, prayer_request: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData?.is_visiting_member || false}
                    onChange={(e) => setEditData({...editData, is_visiting_member: e.target.checked})}
                  />
                }
                label="Visiting Member"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editData?.is_student || false}
                    onChange={(e) => setEditData({...editData, is_student: e.target.checked})}
                  />
                }
                label="Student"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel>Would you require transportation support to come to church?</FormLabel>
                <RadioGroup 
                  row 
                  value={editData?.needs_transportation || false}
                  onChange={(e) => setEditData({...editData, needs_transportation: e.target.value === 'true'})}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {editData?.is_student && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School"
                  value={editData?.school || ''}
                  onChange={(e) => setEditData({...editData, school: e.target.value})}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel>Marital Status</FormLabel>
                <RadioGroup
                  row
                  value={editData?.marital_status || ''}
                  onChange={(e) => setEditData({...editData, marital_status: e.target.value})}
                >
                  <FormControlLabel value="single" control={<Radio />} label="Single" />
                  <FormControlLabel value="married" control={<Radio />} label="Married" />
                  <FormControlLabel value="divorced" control={<Radio />} label="Divorced" />
                  <FormControlLabel value="widowed" control={<Radio />} label="Widowed" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {['is_relocating_member', 'wants_wcito_membership', 'given_life_to_christ', 
              'water_baptized', 'holy_ghost_baptized'].map((field) => (
              <Grid item xs={12} key={field}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editData?.[field] || false}
                      onChange={(e) => setEditData({...editData, [field]: e.target.checked})}
                    />
                  }
                  label={
                    field === 'is_relocating_member' ? 'Relocating Winners Member' :
                    field === 'wants_wcito_membership' ? `Interested in ${organizationName} Membership` :
                    field === 'given_life_to_christ' ? 'Given Life to Christ' :
                    field === 'water_baptized' ? 'Water Baptized' :
                    'Holy Ghost Baptized'
                  }
                />
              </Grid>
            ))}
            {editData?.is_student && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Campus Location"
                    value={editData?.campus_location || ''}
                    onChange={(e) => setEditData({...editData, campus_location: e.target.value})}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel>Campus Duration</FormLabel>
                    <RadioGroup
                      row
                      value={editData?.campus_duration || ''}
                      onChange={(e) => setEditData({...editData, campus_duration: e.target.value})}
                    >
                      <FormControlLabel value="1_year" control={<Radio />} label="1 Year" />
                      <FormControlLabel value="2_years" control={<Radio />} label="2 Years" />
                      <FormControlLabel value="2plus_years" control={<Radio />} label="2+ Years" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the first timer record for{' '}
            {firstTimer?.first_name} {firstTimer?.last_name}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Add this new Dialog component */}
      <Dialog 
        open={completionDialog} 
        onClose={() => {
          setCompletionDialog(false);
          setStatus('contacted');
          // Ensure focus is moved back to the trigger button
          const triggerButton = document.querySelector('[aria-label="Complete Follow-up"]');
          if (triggerButton) {
            triggerButton.focus();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Follow-up Completion</DialogTitle>
        <DialogContent>
          <Typography color="warning.main" sx={{ mb: 2 }}>
            Please note: Once marked as completed, this follow-up cannot be edited further.
          </Typography>
          <Typography sx={{ mb: 2 }}>
            Only mark as completed if no further follow-up is needed. This should only be used in these specific cases:
          </Typography>
          <Box sx={{ mb: 3, bgcolor: 'info.main', color: 'white', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>Established</strong> - They are now regularly attending and actively involved
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>Non-permanent Visitor</strong> - They have confirmed they were just visiting
            </Typography>
            <Typography variant="body2">
              • <strong>Unreachable</strong> - Multiple attempts made with no success
            </Typography>
          </Box>
          <Typography color="info.main" sx={{ mb: 2 }}>
            For all other cases (e.g., needs more follow-up, interested but not yet established), please use the "Contacted" status instead.
          </Typography>
          <FormControl required fullWidth>
            <RadioGroup
              value={completionReason}
              onChange={(e) => setCompletionReason(e.target.value)}
            >
              <FormControlLabel 
                value="established" 
                control={<Radio />} 
                label="Established - Actively attending church and integrated into a department/homecell" 
              />
              <FormControlLabel 
                value="non_permanent_visitor" 
                control={<Radio />} 
                label="Non-permanent Visitor - Confirmed they were just visiting" 
              />
              <FormControlLabel 
                value="not_reachable" 
                control={<Radio />} 
                label="Unreachable - Multiple attempts made but cannot be reached" 
              />
            </RadioGroup>
          </FormControl>
          {!completionReason && (
            <FormHelperText error>
              Please select a reason for completion
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCompletionDialog(false);
            setStatus('contacted');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleCompletionConfirm}
            variant="contained"
            disabled={!completionReason}
          >
            Confirm Completion
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FirstTimerDetail; 